import {POPUP} from "back/commons/widgets/popups";
import {METAS} from "back/edit/wed/features/metaBar";
import {TxtSelMgr} from "back/edit/wed/features/txtSel";
import {WedEditAction} from "back/edit/wed/wedEditor";
import {ITxtElement, ITxtRootContext, ITxtTableContext, TxtRoot, TxtSLModel, TxtULModel} from "back/edit/wed/wedlets/txt/txt";
import {InlElement, InlLeaf, InlWrap, TxtBlock, TxtCaption, TxtElement, TxtLI, TxtMember, TxtParaLike, TxtSL, TxtStr} from "back/edit/wed/wedlets/txt/txtTags";
import {
	buildXa,
	deleteWebRange,
	findTxtEltFirstChild,
	findTxtEltNextSibling,
	findTxtEltOrRootParent,
	findTxtEltParent,
	findTxtEltPreviousSibling,
	findTxtParaLikeFrom,
	findTxtParaSiblingFrom,
	findTxtStrParent,
	IS_TxtElementReal,
	IS_TxtParaParent,
	splitPara,
	unwrapContainerFromSel,
	unwrapInl,
	unwrapInlFromSel
} from "back/edit/wed/wedlets/txt/txtUtils";
import {IElementWedlet, IVirtualisableWedlet, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {ACTION, Action, ActionMenu, ActionMenuDep, IActionToggle} from "lib/commons/actions";
import {IReg, REG} from 'lib/commons/registry';
import {DOM, ENodeType, INodeFilter} from "lib/commons/xml/dom";
import {IJmlSet, JML} from "lib/commons/xml/jml";
import {IXAddr, XA} from "lib/commons/xml/xAddr";
import {IDocBatch, IDocHolderSync} from "lib/edit/docHolder";
import {XmlBatch} from "lib/edit/ot/xmlHouse";
import {DOMSH} from "lib/commons/xml/domsh";
import {ActionBtn} from "back/commons/widgets/buttons";
import {Desk} from "lib/commons/desk";

/**
 * Actions pour le texte riche, initialisables via le WED.
 */
export interface ITxtAction<C extends ITxtRootContext> extends Action<C> {
	initFromWed(elt: Element, reg: IReg<any>, addToFirstList?: string[]): void;
}

interface TxtActionCtor<C extends ITxtRootContext> {
	prototype: ITxtAction<C>;

	new(id: string): ITxtAction<C>;
}

const TXT_ACTIONS_REG: Dict<TxtActionCtor<ITxtRootContext | ITxtTableContext>> = {'none': null};

export function registerTxtAction(localName: string, ctor: any) {
	TXT_ACTIONS_REG[localName] = ctor;
}

export function createTxtAction<C extends ITxtRootContext>(elt: Element, actionId: string, reg: IReg<any>, addUiProps: boolean, addToFirstList?: string[]): ITxtAction<C> {
	const actionSvc = reg.getSvc(actionId);
	if (actionSvc) return actionSvc as ITxtAction<C>;
	if (elt == null)
		throw Error("Action not found: " + actionId);

	const ctor = TXT_ACTIONS_REG[elt.localName];
	if (!ctor) {
		if (elt.localName in TXT_ACTIONS_REG) return null; //'none', suppression explicite.
		throw Error("Txt action executor not found: " + elt.localName);
	}
	const action = new ctor(actionId) as ITxtAction<C>;
	if (addUiProps) {
		const btn = elt.parentElement;
		const label = btn.getAttribute('label');
		if (label) action.setLabel(label);
		const icon = btn.getAttribute('icon');
		if (icon) action.setIcon(icon);
		const group = btn.getAttribute('group');
		if (group) action.setGroup(group);
	}
	if (addToFirstList) {
		const btn = elt.parentElement;
		reg.addToList(addToFirstList[0], actionId, REG.LEVELAUTH_MODEL, action, btn.hasAttribute('order') ? parseInt(btn.getAttribute('order'), 10) : DOM.computeOffset(btn) * 10);
	}
	action.initFromWed(elt, reg, addToFirstList);
	return action;
}


class TxtActionMenu<C extends ITxtRootContext> extends ActionMenuDep<C> implements ITxtAction<C> {

	initFromWed(elt: Element, reg: IReg<any>, addToFirstList?: string[]): void {
		this._actions = [];
		const attLists = elt.getAttribute("lists");
		const lists = attLists ? attLists.split(' ') : null;
		let actions: ITxtAction<C>[] = lists ? reg.mergeLists(...lists) : [];
		for (let ch = elt.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (ch.localName === 'button') {
				const action = createTxtAction<C>(ch.firstElementChild, ch.getAttribute('actionId'), reg, true, lists);
				if (actions && action) actions.push(action);
			}
		}
		this._actions = ACTION.injectSepByGroup(actions, "", null);
	}
}

registerTxtAction("menu", TxtActionMenu);


class TxtInlineMarker extends Action<ITxtRootContext> implements ITxtAction<ITxtRootContext>, IActionToggle<ITxtRootContext> {

	initFromWed(elt: Element) {}

	isVisible(ctx: ITxtRootContext) {
		if (!ctx.txtRoot.txtRootProps.inlineMarkStyle) return false;
		return super.isVisible(ctx);
	}

	isToggle(ctx: ITxtRootContext): this is IActionToggle<ITxtRootContext> {return true}

	getDatas(api: 'toggle', ctx: ITxtRootContext): boolean {
		return ctx.txtRoot.inlineMarkers;
	}

	execute(ctx: ITxtRootContext, ev?: Event) {
		ctx.txtRoot.selMgr.restoreSel();
		const root = ctx.wedMgr.wedEditor.rootNode;
		const newState = !ctx.txtRoot.inlineMarkers;
		DOMSH.findFlatNext(root, root, (n: Node): n is Node => {
			if (n instanceof TxtRoot) n.inlineMarkers = newState;
			return false;
		});
		ctx.txtRoot.storeInlineMarkers();
	}
}

registerTxtAction("inlineMarker", TxtInlineMarker);


export abstract class TxtInsertAction<C extends ITxtRootContext> extends WedEditAction<C> implements ITxtAction<C> {

	tagName: string;
	role: string;

	isEnabled(ctx: C): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		return super.isEnabled(ctx);
	}

	initFromWed(elt: Element) {
		this.tagName = elt.getAttribute("tagName");
		this.role = elt.getAttribute("role");
		if (elt.hasAttribute("enabledPerms")) this.requireEnabledPerm(elt.getAttribute("enabledPerms").split(" "));
		if (elt.hasAttribute("visiblePerms")) this.requireVisiblePerm(elt.getAttribute("visiblePerms").split(" "));
	}

	/** A appeler après un traitement asynchrone (dialogue...) au cours du processus de modification pour vérifier que le contexte n'a pas évolué. */
	static isInsertPointLost(ctx: ITxtRootContext, anchor: ITxtElement, selMgr: TxtSelMgr): boolean {
		selMgr.restoreSel();
		if (anchor.isConnected && DOM.isAncestor(anchor, selMgr.focusNode)) return false;
		//édition concurrente, structure perdue
		POPUP.showNotifInfo("Une modification concurrente a éliminé ce contexte d'insertion.", ctx.wedMgr.wedEditor);
		return true;
	}

}

/**
 * Insert tout élément susceptible d'être frère d'un paragraphe.
 * S'appuie sur le caret courant pour le point d'insertion.
 * Si la sélection n'est pas collapsed, son contenu est supprimé
 * avant l'insertion.
 * Adapté aux blocs terminaux de types images, audio, video et autres liens 'as paragraph'.
 */
export class InsertParaSibling extends TxtInsertAction<ITxtRootContext> {

	static doInsert(jml: IJmlSet, ctx: ITxtRootContext, selMgr: TxtSelMgr, ev?: Event) {
		const selType = selMgr.type;
		if (selType !== 'None') {
			const batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(selMgr.getXaRange());
			//on cherche le contexte initial et suppr l'éventuelle sel courante
			let container: Node;
			let insertBefore: Node;
			let insertAfter: TxtElement;
			if (selType === 'Range') {
				const range = selMgr.range;
				const startPara = findTxtParaLikeFrom(range.startContainer);
				const endPara = findTxtParaLikeFrom(range.endContainer);
				if (startPara === endPara) {
					//sel dans le para
					container = startPara.parentNode;
					if (startPara.isXmlStartOffset(range.startContainer, range.startOffset)) {
						//en début de para
						insertBefore = startPara;
					} else if (startPara.isXmlEndOffset(range.endContainer, range.endOffset)) {
						//en fin de para
						insertAfter = endPara;
					} else {
						//On doit splitter le paragraphe.
						if (IS_TxtParaParent(container) || container === ctx.txtRoot) {
							splitPara(ctx.txtRoot, endPara, range.endContainer, range.endOffset, batch);
						}
						insertAfter = startPara;
					}
				} else {
					//sel inter-para : endContainer permet de s'injecter entre les 2 para restant après la suppr de la sel.
					container = endPara.parentNode;
					insertBefore = endPara;
				}
				deleteWebRange(ctx.txtRoot, range, batch);
			} else if (selType === 'Caret') {
				//split au niveau du caret
				const focusNode = selMgr.focusNode;
				const para = findTxtParaLikeFrom(focusNode);
				if (para) {
					container = para.parentNode;
					if (para.isXmlStartOffset(focusNode, selMgr.focusOffset)) {
						//en début de para
						insertBefore = para;
					} else if (para.isXmlEndOffset(focusNode, selMgr.focusOffset)) {
						//en fin de para
						insertAfter = para;
					} else {
						if (IS_TxtParaParent(container) || container === ctx.txtRoot) {
							splitPara(ctx.txtRoot, para, focusNode, selMgr.focusOffset, batch);
						}
						insertAfter = para;
					}
				} else {
					container = selMgr.focusNode;
					insertBefore = container.childNodes[selMgr.focusOffset];
				}
			} else {
				//selType === 'Object'
				const child = selMgr.focusObject;
				if (!child) return;
				container = child.parentNode;
				insertBefore = child;
			}
			//on remonte jusqu'au container (ie parent des paraSiblings).
			while (!IS_TxtParaParent(container) && container !== ctx.txtRoot) {
				insertBefore = container;
				container = container.parentNode;
				if (!container) return;
			}
			//on insère
			const parentWedlet = (container as IElementWedlet).wedlet;
			if (parentWedlet.isVirtual()) {
				WEDLET.insertDatasFromDisplay(parentWedlet, batch, jml);
			} else {
				let xa: IXAddr;
				if (insertBefore) {
					xa = buildXa(insertBefore);
				} else if (insertAfter) {
					xa = insertAfter.wedAnchor;
					if (!insertAfter.isVirtual()) xa = XA.incrAtDepth(xa, -1, 1);
				} else {
					const lastCh = DOM.findLastChild(container, IS_TxtElementReal);
					xa = XA.append(buildXa(container), lastCh ? lastCh.xaPart + 1 : 0);
				}
				batch.insertJml(xa, jml);
			}
			batch.doBatch();
		}
	}

	static findContainer(ctx: ITxtRootContext, selMgr: TxtSelMgr): ITxtElement {
		const selType = selMgr.type;
		if (selType !== 'None') {
			const focus = selType === 'Range' ? findTxtParaLikeFrom(selMgr.range.startContainer) : selType === 'Caret' ? selMgr.focusNode : selMgr.focusObject;
			return DOM.findParent(focus, ctx.txtRoot, IS_TxtParaParent) || ctx.txtRoot;
		}
		return null;
	}

	async execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		const ctn = InsertParaSibling.findContainer(ctx, selMgr);
		if (ctn) {
			const model = ctn.getTxtWedModel(this.tagName, this.role);
			let newJml: IJmlSet;
			if (model.hasOnCreateHook) {
				newJml = await model.callOnCreateHook(ctn);
				if (!newJml || TxtInsertAction.isInsertPointLost(ctx, ctn, selMgr)) return;//abandon
			} else {
				newJml = [model.newJml()];
			}
			InsertParaSibling.doInsert(newJml, ctx, selMgr, ev);
		}
	}

}

registerTxtAction("insertParaSibling", InsertParaSibling);

/**
 *
 */
class InsertList extends InsertParaSibling {

	async execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		const doc = ctx.txtRoot.wedlet.wedMgr.docHolder;
		let batch = doc.newBatch(selMgr.getXaRange());
		let listModel: TxtULModel, jml: IJmlSet, insBefBlock: TxtBlock;
		[listModel, jml, insBefBlock] = this.prepare(ctx, doc, batch, selMgr);
		if (listModel && insBefBlock) {
			const ctn = findTxtEltParent(insBefBlock);
			if (listModel.hasOnCreateHook) {
				const newJml = await listModel.callOnCreateHook(ctn || ctx.txtRoot);
				if (!newJml || TxtInsertAction.isInsertPointLost(ctx, ctn || ctx.txtRoot, selMgr)) return;//abandon
				//On recalcul au cas où le contenu aurait changé
				batch = doc.newBatch(selMgr.getXaRange());
				let newListModel: TxtULModel;
				[newListModel, jml, insBefBlock] = this.prepare(ctx, doc, batch, selMgr);
				if (newListModel !== listModel || !insBefBlock) return;
				if (newJml.length > 1) (newJml[1] as IJmlSet).push(...jml);
				else newJml.push(jml);
				jml = newJml;
			} else {
				jml = [listModel.newJml(), jml];
			}
			if (insBefBlock instanceof TxtLI) {
				//on remplace un li, on le préserve
				jml = [insBefBlock.model.newJml(), jml];
			}
			if (ctn ? ctn.isVirtual() : ctx.txtRoot.wedlet.isVirtual()) {
				WEDLET.insertDatasFromDisplay((ctn || ctx.txtRoot.wedlet) as IVirtualisableWedlet, batch, jml);
			} else {
				const anchor = insBefBlock.wedAnchor;
				batch.insertJml(anchor, jml);
			}
			batch.doBatch();
		}
	}

	protected prepare(ctx: ITxtRootContext, doc: IDocHolderSync, batch: IDocBatch & XmlBatch, selMgr: TxtSelMgr): [TxtULModel, IJmlSet, TxtBlock] {
		let listModel: TxtULModel, jml: IJmlSet, insBefBlock: TxtBlock;
		const fromPoint = findTxtParaSiblingFrom(selMgr.focusNode);
		if (!fromPoint) return [null, null, null];
		if (selMgr.type !== 'Range') {
			insBefBlock = fromPoint;
			const parent = findTxtEltOrRootParent(insBefBlock);
			listModel = parent.getTxtWedModel(this.tagName, this.role) as TxtULModel;
			if (!fromPoint.isVirtual()) {
				if (listModel) {
					const anchor = fromPoint.wedAnchor;
					jml = [listModel.itemModel.newJml(), doc.getContent(anchor)];
					batch.deleteSequence(anchor, 1);
				}
			} else {
				jml = [];
			}
		} else {
			let entries = [] as TxtBlock[];
			let minDepth = Number.MAX_SAFE_INTEGER;
			//recherche des isParaSibling, Caption, Member et LI dans la sélection
			selMgr.iterateNodesInSel((n: Node) => {
				//console.log("iterateSel:::", n);
				let block = DOM.findParentOrSelf<TxtBlock>(n, ctx.txtRoot, ((n: Node) => (n instanceof TxtParaLike) || (n as TxtElement).isParaSibling || (n instanceof TxtLI)) as INodeFilter<TxtBlock>);
				if (block && block !== entries[entries.length - 1] && !block.isVirtual()) {
					if (!findTxtEltNextSibling(block) && block.isParaSibling && block.parentNode instanceof TxtLI) {
						//peut-etre pouvons-nous privilégier le Li parent si tous les fils sont sélectionnés.
						let prev = findTxtEltPreviousSibling(block);
						let idx = entries.length - 1;
						while (prev && entries[idx--] === prev) prev = findTxtEltPreviousSibling(prev);
						if (!prev) {
							//on peut remonter au LI
							entries.length = idx + 1;
							block = block.parentNode;
							if (block === entries[entries.length - 1]) return [null, null, null];
						}
					}
					const depth = DOM.computeDepth(block);
					if (depth < minDepth) {
						insBefBlock = block;
						minDepth = depth;
					}
					entries.push(block as TxtBlock);
				}
			});
			//console.log("entries", entries);
			if (entries.length === 0) {
				//Sélection de virtuals
				jml = [];
				insBefBlock = fromPoint;
			} else {
				//recherche des structs à supprimer.
				let toDel = [] as TxtBlock[];
				for (let i = 0; i < entries.length; i++) {
					const entry = entries[i];
					toDel.push(entry);
					//on tente d'absorber les listes entières pour la suppression.
					if (entry instanceof TxtLI || entry instanceof TxtCaption || entry instanceof TxtMember) {
						if (!findTxtEltPreviousSibling(entry)) {
							let idx = i + 1;
							let next = findTxtEltNextSibling(entry);
							while (next) {
								if (next !== entries[idx++]) break;
								next = findTxtEltNextSibling(next);
							}
							if (!next) {
								//la sel contient toutes les entrées de la liste, on peut supprimer la liste à la place.
								const parent = findTxtEltParent(entry);
								toDel[toDel.length - 1] = parent;
								i = idx - 1;
								const depth = DOM.computeDepth(parent);
								if (depth < minDepth) {
									insBefBlock = parent;
									minDepth = depth;
								}
							}
						}
					}
				}
				//console.log("replacedBlock", replacedBlock);
				if (insBefBlock instanceof TxtLI) {
					//On remplace un item de liste, on en réinsèrera un nouveau qui contiendra la nouvelle liste.
					listModel = insBefBlock.getTxtWedModel(this.tagName, this.role) as TxtULModel;
				} else {
					if (insBefBlock instanceof TxtMember) {
						//On prévoit de remplacer un Member d'une SL, impossible => extension du remplacement à toute la liste simple.
						const sl = findTxtEltParent(insBefBlock) as TxtSL;
						//on recree entries et toDel
						entries = [];
						for (let ch = findTxtEltFirstChild(sl); ch; ch = findTxtEltNextSibling(ch)) entries.push(ch);
						toDel = [sl];
						insBefBlock = sl;
					}
					listModel = findTxtEltOrRootParent(insBefBlock).getTxtWedModel(this.tagName, this.role) as TxtULModel;
				}

				if (listModel) {
					//on donne les ordres de suppression.
					//console.log("toDel", toDel);
					for (let i = toDel.length - 1; i >= 0; i--) {
						const node = toDel[i];
						batch.deleteSequence(node.wedAnchor, 1);
					}
					batch.needAdjustForNextAdds();
					//construction du contenu de la liste.
					jml = [];
					for (let i = 0; i < entries.length; i++) {
						const entry = entries[i];
						if (entry.isParaSibling) {
							jml.push(listModel.itemModel.newJml(), doc.getContent(entry.wedAnchor));
						} else if (entry instanceof TxtLI) {
							jml.push(...doc.getContent(entry.wedAnchor));
						} else {
							//member, caption
							if (i === 0 && entry instanceof TxtCaption && listModel.titleModel != null) {
								jml.push(listModel.titleModel.newJml(), entry.exportContent());
							} else {
								jml.push(listModel.itemModel.newJml(), [listModel.itemModel.paraModel.newJml(), entry.exportContent()]);
							}
						}
					}
					//console.log("newContent", newContent);
				}
			}
		}
		return [listModel, jml, insBefBlock];
	}

}

registerTxtAction("insertList", InsertList);


/**
 *
 */
class InsertSimpleList extends InsertParaSibling {

	async execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		let batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(selMgr.getXaRange());
		let jml: IJmlSet, insBefBlock: TxtBlock, listModel: TxtSLModel;
		[listModel, jml, insBefBlock] = this.prepare(ctx, batch, selMgr);
		if (listModel && insBefBlock) {
			const ctn = findTxtEltParent(insBefBlock);
			if (listModel.hasOnCreateHook) {
				const newJml = await listModel.callOnCreateHook(ctn || ctx.txtRoot);
				if (!newJml || TxtInsertAction.isInsertPointLost(ctx, ctn || ctx.txtRoot, selMgr)) return;//abandon
				//On recalcul au cas où le contenu aurait changé
				batch = ctx.txtRoot.wedlet.wedMgr.docHolder.newBatch(selMgr.getXaRange());
				let newListModel: TxtSLModel;
				[newListModel, jml, insBefBlock] = this.prepare(ctx, batch, selMgr);
				if (newListModel !== listModel || !insBefBlock) return;
				if (newJml.length > 1) (newJml[1] as IJmlSet).push(...jml);
				else newJml.push(jml);
				jml = newJml;
			} else {
				jml = [listModel.newJml(), jml];
			}
			if (insBefBlock instanceof TxtLI) {
				//on enveloppe le tout dans un LI.
				jml = [insBefBlock.model.newJml(), jml];
			}
			if (ctn ? ctn.isVirtual() : ctx.txtRoot.wedlet.isVirtual()) {
				WEDLET.insertDatasFromDisplay((ctn || ctx.txtRoot.wedlet) as IVirtualisableWedlet, batch, jml);
			} else {
				const anchor = insBefBlock.wedAnchor;
				batch.insertJml(anchor, jml);
			}
			batch.doBatch();
		}
	}

	protected prepare(ctx: ITxtRootContext, batch: IDocBatch & XmlBatch, selMgr: TxtSelMgr): [TxtSLModel, IJmlSet, TxtBlock] {
		let listModel: TxtSLModel, jml: IJmlSet, insBefBlock: TxtBlock;
		const fromPoint = findTxtParaSiblingFrom(selMgr.focusNode);
		if (!fromPoint) return [null, null, null];
		if (selMgr.type !== 'Range') {
			insBefBlock = fromPoint;
			const parent = findTxtEltOrRootParent(insBefBlock);
			listModel = parent.getTxtWedModel(this.tagName, this.role) as TxtSLModel;
			if (!fromPoint.isVirtual()) {
				if (listModel) {
					if (fromPoint instanceof TxtParaLike) {
						const anchor = fromPoint.wedAnchor;
						jml = [listModel.memberModel.newJml(), fromPoint.exportContent()];
						batch.deleteSequence(anchor, 1);
					} else {
						//on est sur un bloc qui ne peut être inséré dans un member d'une liste simple.
						jml = [listModel.memberModel.newJml()];
					}
				}
			} else {
				jml = [];
			}
		} else {
			const entries = [] as TxtBlock[];
			let minDepth = Number.MAX_SAFE_INTEGER;
			//recherche des Para, Caption, Member dans la sélection
			selMgr.iterateNodesInSel((n: Node) => {
				//console.log("iterateSel:::", n);
				let para = DOM.findParentOrSelf<TxtBlock>(n, ctx.txtRoot, ((n: Node) => n instanceof TxtParaLike) as INodeFilter<TxtBlock>);
				if (!para) {
					const tw = document.createTreeWalker(n, ENodeType.element, {
						acceptNode(n: Node) {
							return n instanceof TxtParaLike ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
						}
					});
					for (para = tw.firstChild() as TxtParaLike; para; para = tw.nextSibling() as TxtParaLike) {
						const depth = DOM.computeDepth(para);
						if (depth < minDepth) {
							insBefBlock = para;
							minDepth = depth;
						}
						entries.push(para);
					}
				} else if (para !== entries[entries.length - 1] && !para.isVirtual()) {
					const depth = DOM.computeDepth(para);
					if (depth < minDepth) {
						insBefBlock = para;
						minDepth = depth;
					}
					entries.push(para as TxtBlock);
				}
			});
			//console.log("entries", entries);
			if (entries.length === 0) {
				//Sélection de virtuals
				jml = [];
				insBefBlock = fromPoint;
			} else {
				//recherche des structs à supprimer.
				const toDel = [] as TxtBlock[];
				for (let i = 0; i < entries.length; i++) {
					const entry = entries[i];
					toDel.push(entry);
					//on tente d'absorber les listes entières pour la suppression.
					const parent = findTxtEltParent(entry);
					if (parent instanceof TxtLI || parent instanceof TxtSL) {
						if (!findTxtEltPreviousSibling(entry)) {
							let idx = i + 1;
							let next = findTxtEltNextSibling(entry);
							while (next) {
								if (next !== entries[idx++]) break;
								next = findTxtEltNextSibling(next);
							}
							if (!next) {
								//la sel contient toutes les entrées de ce container, on peut supprimer ce container à la place.
								toDel[toDel.length - 1] = parent;
								i = idx - 1;
								const depth = DOM.computeDepth(parent);
								if (depth < minDepth) {
									insBefBlock = parent;
									minDepth = depth;
								}
							}
						}
					}
				}
				//2ème tour pour tenter de remonter des LI aux UL/OL
				for (let i = 0; i < toDel.length; i++) {
					const node = toDel[i];
					if ((node instanceof TxtLI || node instanceof TxtCaption) && !findTxtEltPreviousSibling(node)) {
						let idx = i + 1;
						let next = findTxtEltNextSibling(node);
						while (next) {
							if (next !== toDel[idx++]) break;
							next = findTxtEltNextSibling(next);
						}
						if (!next) {
							//la sel contient toutes les entrées de ce container, on peut supprimer ce container à la place.
							const parent = findTxtEltParent(node);
							toDel.splice(i, idx - i, parent);
							const depth = DOM.computeDepth(parent);
							if (depth < minDepth) {
								insBefBlock = parent;
								minDepth = depth;
							}
						}
					}
				}
				//On trouve le point dinsertion définitif.
				if (!insBefBlock.isParaSibling) {
					if (insBefBlock instanceof TxtLI) {
						//on va devoir encapsuler le newContent dans un LI
						listModel = insBefBlock.getTxtWedModel(this.tagName, this.role) as TxtSLModel;
					} else {
						//On est dans un liste simple (member ou caption) ou une autre struct avec caption: on en sort.
						insBefBlock = findTxtEltParent(insBefBlock);
						while (insBefBlock && !insBefBlock.isParaSibling) insBefBlock = findTxtEltParent(insBefBlock);
						listModel = insBefBlock ? findTxtEltOrRootParent(insBefBlock).getTxtWedModel(this.tagName, this.role) as TxtSLModel : null;
					}
				} else {
					listModel = findTxtEltOrRootParent(insBefBlock).getTxtWedModel(this.tagName, this.role) as TxtSLModel;
				}
				//console.log("replacedBlock", insertPoint);

				if (listModel) {
					//on donne les ordres de suppression.
					//console.log("toDel", toDel);
					for (let i = toDel.length - 1; i >= 0; i--) {
						const node = toDel[i];
						batch.deleteSequence(node.wedAnchor, 1);
					}
					batch.needAdjustForNextAdds();
					//construction du contenu de la liste.
					jml = [];
					for (let i = 0; i < entries.length; i++) {
						const entry = entries[i];
						if (i === 0 && entry instanceof TxtCaption && listModel.titleModel != null) {
							jml.push(listModel.titleModel.newJml(), entry.exportContent());
						} else {
							jml.push(listModel.memberModel.newJml(), entry.exportContent());
						}
					}
					//console.log("newContent", newContent);
				}
			}
		}
		return [listModel, jml, insBefBlock];
	}
}

registerTxtAction("insertSimpleList", InsertSimpleList);


class InsertDiv extends InsertParaSibling {
	async execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		const doc = ctx.txtRoot.wedlet.wedMgr.docHolder;
		//On cherche les blocs à wraper.
		let firstToWrap: TxtBlock;
		let lastToWrap: TxtBlock;
		switch (selMgr.type) {
		case "Range": {
			const rg = selMgr.range;
			const commonAnc = selMgr.commonAncestor;
			let start = rg.startContainer;
			if (start !== commonAnc) while (start.parentNode !== commonAnc) start = start.parentNode;
			let end = rg.endContainer;
			if (end !== commonAnc) while (end.parentNode !== commonAnc) end = end.parentNode;
			firstToWrap = findTxtParaSiblingFrom(start);
			lastToWrap = findTxtParaSiblingFrom(end);
			break;
		}
		case "Object" :
		case "Caret" :
			firstToWrap = lastToWrap = findTxtParaSiblingFrom(selMgr.focusNode);
			break;
		}
		if (!firstToWrap || !lastToWrap) return;
		const ctn = findTxtEltOrRootParent(firstToWrap);
		const divModel = ctn.getTxtWedModel(this.tagName, this.role);
		let newJml: IJmlSet;
		if (divModel.hasOnCreateHook) {
			newJml = await divModel.callOnCreateHook(ctn);
			if (!newJml || TxtInsertAction.isInsertPointLost(ctx, firstToWrap, selMgr) || TxtInsertAction.isInsertPointLost(ctx, lastToWrap, selMgr)) return;//abandon
		} else {
			newJml = [divModel.newJml()];
		}
		const xaIns = firstToWrap.wedAnchor;
		let delLen = lastToWrap.getVirtualXaPart() - firstToWrap.getVirtualXaPart() + 1;
		const batch = doc.newBatch(selMgr.getXaRange());
		if (delLen === 1 && firstToWrap.isVirtual() && lastToWrap.isVirtual()) {
			//pas de wrap de content
			batch.insertJml(xaIns, newJml);
		} else {
			JML.appendChildren(doc.getContent(xaIns, delLen), newJml, 0);
			batch.spliceSequence(xaIns, delLen, newJml);
		}
		batch.doBatch();
	}
}

registerTxtAction("insertDiv", InsertDiv);


class UnwrapList extends WedEditAction<ITxtRootContext> implements ITxtAction<ITxtRootContext> {

	target(): "list" | "div" {return "list"}

	initFromWed(elt: Element) {}

	initButtonNode(buttonNode: Element, ctx: ITxtRootContext) {
		super.initButtonNode(buttonNode, ctx);
		if (buttonNode instanceof ActionBtn) buttonNode.accel = "Shift+Arr.";
	}

	isEnabled(ctx: ITxtRootContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		return super.isEnabled(ctx) && unwrapContainerFromSel(this.target(), ctx.txtRoot, true);
	}

	execute(ctx: ITxtRootContext, ev?: Event) {
		unwrapContainerFromSel(this.target(), ctx.txtRoot, false, true);
	}
}

registerTxtAction("unwrapList", UnwrapList);


class UnwrapDiv extends UnwrapList {
	target(): "list" | "div" {return "div"}
}

registerTxtAction("unwrapDiv", UnwrapDiv);


class UnwrapInline extends WedEditAction<ITxtRootContext> implements ITxtAction<ITxtRootContext> {

	initFromWed(elt: Element) {}

	isEnabled(ctx: ITxtRootContext): boolean {
		if (!WEDLET.isWritableWedlet(ctx.txtRoot.wedlet)) return false;
		return super.isEnabled(ctx) && unwrapInlFromSel(ctx.txtRoot, true);
	}

	execute(ctx: ITxtRootContext, ev?: Event) {
		unwrapInlFromSel(ctx.txtRoot, false);
	}
}

registerTxtAction("unwrapInline", UnwrapInline);


abstract class InsertInl extends TxtInsertAction<ITxtRootContext> {

	protected async insertTagAtCaret(ctx: ITxtRootContext, selMgr: TxtSelMgr) {
		const focusNode = selMgr.focusNode;
		const txtStr = findTxtStrParent(focusNode, true);
		if (txtStr) {
			const parent = findTxtEltParent(txtStr);
			const model = parent.getTxtWedModel(this.tagName, this.role);
			if (model) {
				let newJml: IJmlSet;
				if (model.hasOnCreateHook) {
					newJml = await model.callOnCreateHook(parent);
					if (!newJml || TxtInsertAction.isInsertPointLost(ctx, txtStr, selMgr)) return;//abandon
				} else {
					newJml = [model.newJml()];
				}
				const doc = ctx.wedMgr.docHolder;
				if (parent.isVirtual()) {
					const batch = doc.newBatch(selMgr.getXaRange());
					WEDLET.insertDatasFromDisplay(parent, batch, newJml);
					batch.setSelAfter(txtStr.wedAnchor).doBatch();
				} else if (txtStr.isVirtual()) {
					doc.newBatch(selMgr.getXaRange()).insertJml(txtStr.wedAnchor, newJml).doBatch();
				} else {
					const xmlOffset = txtStr.getXmlOffset(focusNode, selMgr.focusOffset);
					doc.newBatch(selMgr.getXaRange()).splitText(XA.append(txtStr.wedAnchor, xmlOffset), 0, txtStr.getXmlText().substring(xmlOffset), newJml).doBatch();
				}
			}
		}
	}

	protected async wrapObject(ctx: ITxtRootContext, selMgr: TxtSelMgr) {
		const node = findTxtEltParent(selMgr.focusNode, true);
		const ctn = findTxtEltParent(node);
		if (ctn) {
			const model = ctn.getTxtWedModel(this.tagName, this.role);
			if (model) {
				let newJml: IJmlSet;
				if (model.hasOnCreateHook) {
					newJml = await model.callOnCreateHook(ctn);
					if (!newJml || TxtInsertAction.isInsertPointLost(ctx, node, selMgr)) return;//abandon
				} else {
					newJml = [model.newJml()];
				}
				const doc = ctx.wedMgr.docHolder;
				JML.appendChildren(doc.getContent(node.wedAnchor), newJml, 0);
				doc.newBatch(selMgr.getXaRange())
					.spliceSequence(node.wedAnchor, 1, newJml)
					.doBatch();
				return;
			}
		}
	}
}


/** Insertion d'objets terminaux inline (img, res, emptyleaf...) */
class InsertInlObject extends InsertInl {

	isEnabled(ctx: ITxtRootContext): boolean {
		return ctx.txtRoot.selMgrAsIs.type === 'Caret' && super.isEnabled(ctx);
	}

	execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		if (selMgr.type === 'Caret') this.insertTagAtCaret(ctx, selMgr)
	}
}

registerTxtAction("insertInlObject", InsertInlObject);

/**
 * Insertion / suppression de balise inline n'acceptant que du texte à l'intérieur.
 * Cette action permet l'injection ou la suppression d'une multitude de balises, y compris
 * avec une sélection inter-paragraphes.
 * Mal adapté si la balise possède des metas.
 */
class ToggleTextLeafMulti extends InsertInl implements IActionToggle<ITxtRootContext> {

	isToggle(ctx: ITxtRootContext): this is IActionToggle<ITxtRootContext> {return true}

	isEnabled(ctx: ITxtRootContext): boolean {
		if (!super.isEnabled(ctx)) return false;
		//si on peut ajouter ou supprimer ce texteLeaf aux points anchor ou focus, on active.
		const selMgr = ctx.txtRoot.selMgrAsIs;
		switch (selMgr.type) {
		case 'Range':
			let anchNode = findTxtEltOrRootParent(selMgr.anchorNode, true);
			if (anchNode instanceof TxtStr) anchNode = findTxtEltOrRootParent(anchNode);
			if (anchNode instanceof InlLeaf) {
				if (anchNode.model.nodeName === this.tagName && anchNode.model.role === this.role) return true;
			} else if (anchNode && anchNode.getTxtWedModel(this.tagName, this.role) != null) return true;
		case 'Caret':
			let focNode = findTxtEltOrRootParent(selMgr.focusNode, true);
			if (focNode instanceof TxtStr) focNode = findTxtEltOrRootParent(focNode);
			if (focNode instanceof InlLeaf) return focNode.model.nodeName === this.tagName && focNode.model.role === this.role; //toggle off.
			return focNode ? focNode.getTxtWedModel(this.tagName, this.role) != null : false;
		default : //'Object' et 'None'
			return false;
		}
	}

	getDatas(api: 'toggle', ctx: ITxtRootContext): boolean {
		//On évalue anchor et focus car au undo/redo les deux points peuvent être inversés ce qui donne un résultat différent.
		const selMgr = ctx.txtRoot.selMgr;
		if (selMgr.type === 'None') return false;
		if (selMgr.type === 'Range' && selMgr.anchorNode !== selMgr.focusNode) {
			if (DOM.findParentOrSelf(selMgr.anchorNode, ctx.txtRoot, (n: Node): n is Node => {
				return n instanceof InlLeaf && n.model.nodeName === this.tagName && n.model.role === this.role;
			}) !== null) return true;
		}
		return DOM.findParentOrSelf(selMgr.focusNode, ctx.txtRoot, (n: Node): n is Node => {
			return n instanceof InlLeaf && n.model.nodeName === this.tagName && n.model.role === this.role;
		}) !== null;
	}

	execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		if (this.getDatas('toggle', ctx)) {
			//On supprime le tag
			const set = new Set<InlLeaf>();
			selMgr.iterateNodesInSel((node: Node) => {
				//console.log("selMgr.iterateNodesInSel:::", node);
				const txtElt = DOM.findParentOrSelf(node, ctx.txtRoot, (n => {
					return n instanceof InlLeaf && n.model.nodeName === this.tagName && n.model.role === this.role;
				}) as INodeFilter<InlLeaf>);
				if (txtElt) set.add(txtElt);
				else if (node instanceof HTMLElement) {
					for (const leaf of node.querySelectorAll('inl-leaf') as NodeListOf<InlLeaf>) {
						if (leaf.model.nodeName === this.tagName && leaf.model.role === this.role) set.add(leaf);
					}
				}
			});
			if (set.size > 0) {
				const batch = ctx.wedMgr.docHolder.newBatch(selMgr.getXaRange());
				const remMem = new Map<TxtStr, TxtStr>();
				for (const inl of set) unwrapInl(inl, batch, remMem);
				batch.doBatch();
			}
		} else {
			//On insert le tag
			if (selMgr.type === 'Caret') {
				this.insertTagAtCaret(ctx, selMgr);
			} else {
				//selMgr.trim(); géré au doubl-click sur le mot.
				const range = selMgr.range;
				const batch = ctx.wedMgr.docHolder.newBatch(selMgr.getXaRange());
				selMgr.iterateNodesInSel((node: Node) => {
					//console.log("selMgr.iterateNodesInSel:::", node);
					const txtElt = findTxtEltParent(node, true);
					if ((node === range.startContainer || node === range.endContainer) && txtElt instanceof TxtStr) {
						//Cas des noeuds de départ/arrivé.
						if (!txtElt.isVirtual() && (node === range.startContainer || findTxtStrParent(range.startContainer) !== txtElt)) {
							//ce noeud texte existe et est le départ ou la fin (mais différente du départ).
							const model = findTxtEltParent(txtElt).getTxtWedModel(this.tagName, this.role);
							if (model) {
								const text = txtElt.getXmlText();
								const offsetStart = node === range.startContainer ? txtElt.getXmlOffset(node, range.startOffset) : 0;
								const offsetEnd = node === range.endContainer ? txtElt.getXmlOffset(node, range.endOffset) : text.length;
								batch.splitText(XA.append(txtElt.wedAnchor, offsetStart), offsetEnd - offsetStart, text.substring(offsetEnd), [model.newJml(), [text.substring(offsetStart, offsetEnd)]]).needAdjustForNextAdds();
							}
						}
					} else if (node instanceof TxtStr) {
						if (!node.isVirtual()) {
							const model = findTxtEltParent(node).getTxtWedModel(this.tagName, this.role);
							if (model) batch.spliceSequence(node.wedAnchor, 1, [model.newJml(), [node.getXmlText()]]).needAdjustForNextAdds();
						}
					} else if (node instanceof HTMLElement) {
						for (const txtStr of node.querySelectorAll('txt-str') as NodeListOf<TxtStr>) {
							if (!txtElt.isVirtual()) {
								const model = findTxtEltParent(txtStr).getTxtWedModel(this.tagName, this.role);
								if (model) batch.spliceSequence(txtStr.wedAnchor, 1, [model.newJml(), [txtStr.getXmlText()]]).needAdjustForNextAdds();
							}
						}
					}
				});
				if (batch.getState() === 'empty') {
					//TODO notification pas trouvé.
				} else {
					batch.doBatch();
				}
			}
		}
	}
}

registerTxtAction("toggleTextLeafMulti", ToggleTextLeafMulti);


/**
 * Insertion / suppression de balise inline n'acceptant que du texte à l'intérieur.
 * Cette action permet l'injection ou la suppression d'une et une seule balise.
 */
class ToggleTextLeafSingle extends InsertInl implements IActionToggle<ITxtRootContext> {

	isToggle(ctx: ITxtRootContext): this is IActionToggle<ITxtRootContext> {return true}

	isEnabled(ctx: ITxtRootContext): boolean {
		if (!super.isEnabled(ctx)) return false;
		//si on peut ajouter ou supprimer ce texteLeaf aux points anchor ou focus, on active.
		const selMgr = ctx.txtRoot.selMgrAsIs;
		const selType = selMgr.type;
		if (selType === 'Range' || selType === 'Caret') {
			const str = findTxtStrParent(selMgr.focusNode, true);
			if (selType === 'Range' && str !== findTxtStrParent(selMgr.anchorNode, true)) return false;
			const ctn = findTxtEltParent(str);
			if (ctn && ctn.model.nodeName === this.tagName && ctn.model.role === this.role) return true; //on est dans ce leaf
			return ctn && ctn.getTxtWedModel(this.tagName, this.role) != null; //leaf autrisé dans ce ctn.
		}
		return false;
	}

	getDatas(api: 'toggle', ctx: ITxtRootContext): boolean {
		const selMgr = ctx.txtRoot.selMgr;
		if (!selMgr.focusNode) return false;
		const str = findTxtStrParent(selMgr.focusNode, true);
		const ctn = findTxtEltParent(str);
		return ctn instanceof InlWrap && ctn.model.nodeName === this.tagName && ctn.model.role === this.role;
	}

	async execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		const str = findTxtStrParent(selMgr.focusNode, true);
		const ctn = findTxtEltParent(str);
		if (!ctn) return;
		if (ctn instanceof InlWrap && ctn.model.nodeName === this.tagName && ctn.model.role === this.role) {
			//On supprime le tag
			const batch = ctx.txtRoot.wedMgr.docHolder.newBatch(selMgr.getXaRange());
			unwrapInl(ctn, batch);
			batch.doBatch();
		} else {
			//On insert le tag
			if (selMgr.type === 'Caret') {
				this.insertTagAtCaret(ctx.txtRoot, selMgr);
			} else {
				//selMgr.trim(); géré au doubl-click sur le mot.
				const range = selMgr.range;
				const model = ctn.getTxtWedModel(this.tagName, this.role);
				if (model) {
					let newJml: IJmlSet;
					if (model.hasOnCreateHook) {
						newJml = await model.callOnCreateHook(ctn);
						if (!newJml || TxtInsertAction.isInsertPointLost(ctx, ctn, selMgr)) return;//abandon
					} else {
						newJml = [model.newJml()];
					}
					const text = str.getXmlText();
					const offsetStart = str.getXmlOffset(range.startContainer, range.startOffset);
					const offsetEnd = str.getXmlOffset(range.endContainer, range.endOffset);
					JML.appendChildren([text.substring(offsetStart, offsetEnd)], newJml, 0);
					ctx.txtRoot.wedMgr.docHolder.newBatch(selMgr.getXaRange())
						.splitText(XA.append(str.wedAnchor, offsetStart), offsetEnd - offsetStart, text.substring(offsetEnd), newJml)
						.doBatch();
				}
			}
		}
	}
}

registerTxtAction("toggleTextLeafSingle", ToggleTextLeafSingle);


/**
 * Insertion / suppression pour les balises de type styleTag, phraseTag.
 * Cette action permet l'injection ou la suppression d'une et une seule balise à la fois.
 *
 */
export class ToggleSpanSingle extends InsertInl implements ITxtAction<ITxtRootContext>, IActionToggle<ITxtRootContext> {
	isToggle(ctx: ITxtRootContext): this is IActionToggle<ITxtRootContext> {return true}

	isEnabled(ctx: ITxtRootContext): boolean {
		if (!super.isEnabled(ctx)) return false;
		const selMgr = ctx.txtRoot.selMgrAsIs;
		const selType = selMgr.type;
		if (selType === 'Range') {
			//l'insertion se fera toujours au startContainer (pas le focusNode, trop complexe à comprendre pour le user)
			let ctn = findTxtEltParent(selMgr.commonAncestor, true);
			if (ctn instanceof TxtStr) ctn = findTxtEltParent(ctn);
			return ctn && ctn.getTxtWedModel(this.tagName, this.role) != null;
		}
		let ctn: TxtElement;
		if (selType === 'Caret') {
			const str = findTxtStrParent(selMgr.focusNode, true);
			ctn = findTxtEltParent(str);
		} else if (selType === 'Object') {
			ctn = findTxtEltParent(selMgr.focusNode);
		}
		if (ctn && ctn.getTxtWedModel(this.tagName, this.role) != null) return true;
		while (ctn && ctn.isTextContainer) {
			if (ctn.model.nodeName === this.tagName && ctn.model.role === this.role) return true;
			ctn = findTxtEltParent(ctn);
		}
		return false;
	}

	getDatas(api: 'toggle', ctx: ITxtRootContext): boolean {
		return this.findSpan(ctx.txtRoot.selMgr) != null;
	}

	protected findSpan(selMgr: TxtSelMgr): InlWrap | null {
		if (selMgr.type === 'None') return null;
		let ctn = findTxtEltParent(selMgr.commonAncestor, true);
		while (ctn instanceof InlElement || ctn instanceof TxtStr) {
			if (ctn.model.nodeName === this.tagName && ctn.model.role === this.role) return ctn as InlWrap;
			ctn = findTxtEltParent(ctn);
		}
		return null;
	}

	async execute(ctx: ITxtRootContext, ev?: Event) {
		const selMgr = ctx.txtRoot.selMgr.restoreSel();
		const found = this.findSpan(selMgr);
		if (found) {
			//On suprrime ce span
			const batch = ctx.wedMgr.docHolder.newBatch(selMgr.getXaRange());
			unwrapInl(found, batch);
			const prev = findTxtEltPreviousSibling(found);
			if (prev instanceof TxtStr) {
				const offset = prev.getXmlTextLength();
				batch.setSelAfterSkipAdjust(XA.append(prev.wedAnchor, offset));
			} else {
				console.log("prev TxtStr not found???");
				batch.setSelAfterSkipAdjust(found.wedAnchor);
			}
			batch.doBatch();
		} else {
			//On ajoute un span
			const selType = selMgr.type;
			if (selType === 'Range') {
				//selMgr.trim(); géré au doubl-click sur le mot.
				let commonAnc = findTxtEltParent(selMgr.commonAncestor, true);
				if (commonAnc && (commonAnc.isTextContainer || commonAnc instanceof TxtStr)) {
					//La sel n'est pas inter-paragraphe.
					let txtStr: TxtStr;
					if (commonAnc instanceof TxtStr) {
						txtStr = commonAnc;
						commonAnc = findTxtEltParent(commonAnc);
					}
					const model = commonAnc.getTxtWedModel(this.tagName, this.role);
					if (model) {
						const doc = ctx.wedMgr.docHolder;
						let newJml: IJmlSet;
						if (model.hasOnCreateHook) {
							newJml = await model.callOnCreateHook(commonAnc);
							if (!newJml || TxtInsertAction.isInsertPointLost(ctx, commonAnc, selMgr)) return;//abandon
						} else {
							newJml = [model.newJml()];
						}
						const batch = doc.newBatch(selMgr.getXaRange());
						const range = selMgr.getXaRangeInBlock();
						if (newJml.length < 2) newJml.push(doc.extractRange(range, batch, []));
						else doc.extractRange(range, batch, newJml[1] as IJmlSet);
						if (commonAnc.isVirtual()) {
							WEDLET.insertDatasFromDisplay(commonAnc, batch, newJml);
							batch.setSelAfter(range.start).doBatch();
						} else if (txtStr) {
							if (txtStr.isVirtual()) {
								batch.insertJml(range.start, newJml).doBatch();
							} else {
								batch.insertJml(XA.incrAtDepth(txtStr.wedAnchor, -1, 1), newJml).doBatch();
							}
						} else {
							const commonXa = commonAnc.wedAnchor;
							batch.insertJml(XA.append(commonXa, (range.start[commonXa.length] as number) + 1), newJml).doBatch();
						}
					}
				}
			} else if (selType === 'Caret') {
				return this.insertTagAtCaret(ctx, selMgr);
			} else if (selType === 'Object') {
				return this.wrapObject(ctx, selMgr);
			}
		}
	}
}

registerTxtAction("toggleSpanSingle", ToggleSpanSingle);


/** Accès aux métas d'une balise. */
export class OpenMetaDialog extends Action<ITxtRootContext> implements ITxtAction<ITxtRootContext> {

	static openMetaDialog(txtNode: TxtElement | null) {
		if (!txtNode) return;
		const metaDefs = txtNode.getMetaDefs();
		if (metaDefs) {
			const dialog = METAS.openMeta(metaDefs);
			if (dialog) dialog.then(() => {
				if (txtNode.isConnected) txtNode.txtRoot.selMgrAsIs.setSelection(txtNode);
			});
		}
	}

	initFromWed(elt: Element) {}

	isEnabled(ctx: ITxtRootContext): boolean {
		if (!super.isEnabled(ctx)) return false;
		let txtNode = ctx.txtRoot.selMgrAsIs.commonTxtElement;
		if (txtNode instanceof TxtStr) txtNode = findTxtEltParent(txtNode);
		if (!txtNode) return false;
		return txtNode.hasMetaDefs();
	}


	execute(ctx: ITxtRootContext, ev?: Event) {
		let txtNode = ctx.txtRoot.selMgrAsIs.commonTxtElement;
		if (txtNode instanceof TxtStr) txtNode = findTxtEltParent(txtNode);
		OpenMetaDialog.openMetaDialog(txtNode);
	}

	// initButtonNode(buttonNode: ActionBtn<ITxtRootContext>, ctx: ITxtRootContext) {
	// 	if (!isTooltipPointer(buttonNode)) {
	// 		//1er appel à initButtonNode, on crée le tooltip de preview sur le bouton.
	// 		//Peu d'intérêt à priori, plus pour l'exemple... A virer ?
	// 		let newEditor = new WedEditorMiniview().initialize({reg:ctx.wedMgr.reg});
	// 		let wedMgr = ctx.wedMgr;
	// 		newEditor.setWedModel(wedMgr.wedModel);
	// 		//newEditor.onViewShown
	// 		let tooltip = POPUP.attachTooltip(buttonNode, newEditor);
	// 		tooltip.show = function (this: PopupTooltip, anchor?: OPopupAnchor, from?: Node): boolean {
	// 			let txtNode = findTxtEltParent(ctx.txtRoot.selMgr.commonAncestor, true);
	// 			if (txtNode instanceof TxtStr) txtNode = findTxtEltParent(txtNode);
	// 			if (txtNode && txtNode.model.renderingModel) {
	// 				let config = Object.create(wedMgr.config) as OWedManagerConfig;
	// 				config.xaRoot = txtNode.wedAnchor;
	// 				config.rootModel = txtNode.model.renderingModel;
	// 				newEditor.initFromDocHolder(wedMgr.docHolderAsync.cloneDocHolder(), config);
	// 				return PopupTooltip.prototype.show.call(this, anchor, from);
	// 			}
	// 			return false;
	// 		};
	// 		(newEditor as IView).onViewHidden = function () {
	// 			newEditor.wedMgr.detachDocHolder();
	// 		}
	// 	}
	// }
}

registerTxtAction("openMetaDialog", OpenMetaDialog);


// export class TxtEraseDiffSel extends Action<ITxtRootContext> { //implements ITxtAction<ITxtRootContext>
//
// 	static SINGLETON = new TxtEraseDiffSel("eraseDiffSel");
//
// 	//initFromWed(elt: Element) {}
//
// 	constructor(id?: string) {
// 		super(id);
// 		this._group = "diff";
// 	}
//
// 	getIcon(ctx: ITxtRootContext): string {
// 		return ctx.wedMgr.docHolder.getDiffSession()?.mode === EXmlDiffMode.diff ? "/@skin@/edit/wed/diff/revert.svg" : "/@skin@/edit/wed/diff/valid.svg";
// 	}
//
// 	getLabel(ctx: ITxtRootContext): string {
// 		if (ctx.txtRoot.selMgrAsIs.type === 'Caret') return ctx.wedMgr.docHolder.getDiffSession()?.mode === EXmlDiffMode.diff ? "Annuler cet ajout" : "Valider cette suppression";
// 		return ctx.wedMgr.docHolder.getDiffSession()?.mode === EXmlDiffMode.diff ? "Annuler ces modifications" : "Valider ces modifications";
// 	}
//
// 	isVisible(ctx: ITxtRootContext) {
// 		if (!WEDLET.diffLib) return false;
// 		const selMgr = ctx.txtRoot.selMgrAsIs;
// 		const selType = selMgr.type;
// 		if (selType === 'Caret') {
// 			if (!WEDLET.diffLib.findDiffMark(selMgr.focusNode, selMgr.focusOffset)) return false;
// 		}
// 		return super.isVisible(ctx);
// 	}
// }

/** Action de remplacement d'un mot mal orthographié */
class SpellcheckSuggestion extends Action<ITxtRootContext> {
	constructor(readonly word: string) {
		super();
		this._label = word;
		this._group = "dictSuggest"
	}

	execute(ctx: ITxtRootContext, ev?: Event): any {
		const rg = ctx.txtRoot.selMgrAsIs.getXaRange();
		if (rg && XA.isBeforeInSameSeq(rg.start, rg.end)) {
			ctx.wedMgr.docHolder.newBatch().spliceSequence(rg.start, XA.last(rg.end) as number - (XA.last(rg.start) as number), this.word).doBatch();
		}
	}
}

/** Menu des actions de la correction orthographique (remplacements, ajout au dictionnaire) */
export const spellcheckMenu = new ActionMenu<ITxtRootContext>("spellcheck")
	.setLabel("Correction orthographique")
	.setVisible((ctx) => Desk.electron && ACTION.spellcheckSymbol in ctx)
	.setActions((ctx, action) => {
		const actions = [];
		for (const dictSuggest of ctx[ACTION.spellcheckSymbol].dictionarySuggestions) {
			actions.push(new SpellcheckSuggestion(dictSuggest));
		}
		actions.push(addToDict);
		return ACTION.injectSepByGroup(actions, "dictSuggest *", ctx);
	});

// export const copyMenu = new Action<ITxtRootContext>("copy")
// 	.setLabel("Copier")
// 	.setExecute((ctx:ITxtRootContext, ev) => {
// 		if(ev instanceof KeyboardEvent) return 'noStopPropag-noPreventDefault'; //ctrl+C natif via clipboard events
// 		const selMgr = ctx.txtRoot.selMgr;
// 		if (selMgr.tableLayout) selMgr.tableLayout.onCopy();
// 		else ctx.wedMgr.writeRangeToClipboard(selMgr.getXaRange());
// 	});

export const copyAsXml = new Action<ITxtRootContext>("copyXml")
	.setLabel("Copier en XML")
	.setExecute((ctx: ITxtRootContext, ev) => {
		const selMgr = ctx.txtRoot.selMgr;
		if (selMgr.tableLayout) selMgr.tableLayout.onCopyXml();
		else ctx.wedMgr.writeRangeToClipboardAsXml(selMgr.getXaRange());
	});

// export const pasteMenu = new Action<ITxtRootContext>("paste")
// 	.setLabel("Coller")
// 	.setExecute((ctx:ITxtRootContext, ev) => {
// 		if(ev instanceof KeyboardEvent) return 'noStopPropag-noPreventDefault'; //ctrl+V natif via clipboard events
// 		ctx.txtRoot.doPaste();
// 	});

export const pasteAsText = new WedEditAction<ITxtRootContext>("pasteRawText")
	.setLabel("Coller texte brut")
	.setExecute((ctx: ITxtRootContext, ev) => {ctx.txtRoot.doPaste("text")});

export const pasteAsString = new WedEditAction<ITxtRootContext>("pasteRawString")
	.setLabel("Coller texte brut mono-paragraphe")
	.setExecute((ctx: ITxtRootContext, ev) => {ctx.txtRoot.doPaste("string")});

//export const	copyPasteMenus = [copyMenu, copyXmlMenu, pasteMenu, pasteRawMenu];

/** Action d'ajout au dictionnaire */
const addToDict = new Action<ITxtRootContext>("addToDict")
	.setLabel("Ajouter au dictionnaire")
	.setExecute((ctx: ITxtRootContext) => {
		window.postMessage({type: "client:spellcheck:addToDict", word: ctx[ACTION.spellcheckSymbol].misspelledWord}, location.origin);
	});